import DemoAnswers from '@/components/demoComponents/demoUtils/demoAnswers';
import DemoUserInfo from '@/components/demoComponents/demoUtils/demoUserInfo';
import * as _ from "lodash";

export default class DemoGameStates {
  // users
  player: DemoUserInfo
  remoteUser1: DemoUserInfo
  remoteUser2: DemoUserInfo
  remoteUser3: DemoUserInfo
  // ai answers
  demoAnswers: DemoAnswers;
  
  // scoreboard
  playerScore: number;
  remoteScore1: number;
  remoteScore2: number;
  remoteScore3: number;

  // these values are from the environment variables in the backend - we should adjust these if the loading time for animations,
  // answer times, etc are modified
  ROUND_START_BUFFER_DURATION = 4000;
  ANSWER_DURATION = 95000;
  TRANSITION_BUFFER_DURATION = 500;
  VOTE_START_BUFFER_DURATION = 5000;
  NETWORK_DELAY_DURATION = 1000;
  VOTE_DURATION = 35000;
  VOTE_RESULT_DURATION = 10000;
  // answerDelayDuration should be the same as the delay in startGame.ts on the backend
  answerDelayDuration = this.ROUND_START_BUFFER_DURATION + this.ANSWER_DURATION + this.NETWORK_DELAY_DURATION;
  // initialVotingDelayDuration should be the sameas the processAnswer.ts on the backend
  initialVotingDelayDuration = this.TRANSITION_BUFFER_DURATION + this.VOTE_START_BUFFER_DURATION + this.NETWORK_DELAY_DURATION
    + this.TRANSITION_BUFFER_DURATION + this.VOTE_DURATION;
  // subsequentVotingDelayDuration should has some extra time added for showing the vote result from the previous round
  subsequentVotingDelayDuration = this.TRANSITION_BUFFER_DURATION + this.VOTE_START_BUFFER_DURATION + this.NETWORK_DELAY_DURATION
    + this.TRANSITION_BUFFER_DURATION + this.VOTE_DURATION + this.VOTE_RESULT_DURATION;
  
  constructor(playerName: string, remoteName1: string, remoteName2: string, remoteName3: string) {
    this.player = {
      name: playerName,
      id: 'auth0_demo_player_id',
      primary: '#FABA45',
      secondary: '#FFD17D',
      background: '#F5F1E7'
    }
    this.remoteUser1 = {
      name: remoteName1,
      id: 'auth0_demo_remote_id_1',
      primary: '#9DC277',
      secondary: '#C4DEA9',
      background: '#ECF3E4'
    }
    this.remoteUser2 = {
      name: remoteName2,
      id: 'auth0_demo_remote_id_2',
      primary: '#539798',
      secondary: '#96C9CA',
      background: '#E7F1F5'
    }
    this.remoteUser3 = {
      name: remoteName3,
      id: 'auth0_demo_remote_id_3',
      primary: '#6291D8',
      secondary: '#ADC8F1',
      background: '#E4ECF8'
    }
    this.demoAnswers = new DemoAnswers(this.player, this.remoteUser1, this.remoteUser2, this.remoteUser3);
    this.playerScore = 0;
    this.remoteScore1 = 0;
    this.remoteScore2 = 0;
    this.remoteScore3 = 0;
  }
  getAnswerCountdownTimestamp() {
    return new Date(Date.now() + this.answerDelayDuration).toISOString();
  }
  getInitialVotingCountdownTimestamp() {
    return new Date(Date.now() + this.initialVotingDelayDuration).toISOString();
  }
  getSubsequentVotingCountdownTimeStamp() {
    return new Date(Date.now() + this.subsequentVotingDelayDuration).toISOString();
  }
  getBackupPlayerAnswers(submittedAnswers) {
    // used to insert backup prompts if the user didn't submit 2 prompts
    let answers = _.cloneDeep(submittedAnswers);
    if (answers.length == 0) {
      answers.push(this.demoAnswers.getEmptyPlayerAnswer1());
      answers.push(this.demoAnswers.getEmptyPlayerAnswer2());
    } else if (answers.length == 1) {
      answers.push(this.demoAnswers.getEmptyPlayerAnswer2());
    }
    return answers;
  }
  getInitialState() {
    return {
      gameStatus: 'waitingForPlayers',
      roundIndex: -1,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: '',
      playerUsernames: [ this.player.name],
      // dummy auth0 ids just for the demo
      playerUserIds: [ this.player.id ],
      // colours from are the same from the backend -> src/constant/colors.ts
      playerPrimaryColors: [ this.player.primary ],
      playerSecondaryColors: [ this.player.secondary ],
      playerBackgroundColors: [ this.player.background ]
    }
  }
  getRemoteJoin1State() {
    return {
      gameStatus: 'waitingForPlayers',
      roundIndex: -1,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: '',
      playerUsernames: [ this.player.name, this.remoteUser1.name ],
      playerUserIds: [ this.player.id , this.remoteUser1.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background ]
    }
  }
  getRemoteJoin2State() {
    return {
      gameStatus: 'waitingForPlayers',
      roundIndex: -1,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: '',
      playerUsernames: [ this.player.name, this.remoteUser1.name, this.remoteUser2.name ],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background ]
    }
  }
  getRemoteJoin3State() {
    return {
      gameStatus: 'waitingToStart',
      roundIndex: -1,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: '',
      playerUsernames: [ this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name ],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ]
    }
  }
  getGameStartState() {
    return {
      gameStatus: 'playing',
      roundIndex: 0,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getAnswerCountdownTimestamp(),
      playerUsernames: [ this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name ],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      prompts: [
        {
          roundIndex: 0,
          turnIndex: 1,
          answerId: 1,
          promptText: "When someone sends you an email and cc's your boss",
          replyType: 'gif'
        },
        {
          roundIndex: 0,
          turnIndex: 2,
          answerId: 0,
          promptText: 'Me clicking "Leave Meeting" while everyone is saying goodbye',
          replyType: 'gif'
        }
      ]
    }
  }
  getVotingState0(playerAnswer) {
    return {
      gameStatus: 'playing',
      roundIndex: 0,
      turnIndex: 0,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getInitialVotingCountdownTimestamp(),
      playerUsernames: [this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      turnInfo: {
        answers: [
          playerAnswer,
          this.demoAnswers.getRemoteUser1Answer1()
        ],
        canPlayerVote: false,
        promptText: this.demoAnswers.getRemoteUser1Answer1().instantiatedPromptText,
        roundIndex: 0,
        turnIndex: 0,
        wereAllAnswersSubmitted: this.demoAnswers.didPlayerSubmitResponse[0]
      }
    }
  }
  getVotingState1(prevPlayerAnswer, playerAnswer) {
    let remoteAnswer1 = this.demoAnswers.getRemoteUser1Answer1();
    this.playerScore = this.playerScore + prevPlayerAnswer.score;
    this.remoteScore1 = this.remoteScore1 + remoteAnswer1.score;
    return {
      gameStatus: 'playing',
      roundIndex: 0,
      turnIndex: 1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getSubsequentVotingCountdownTimeStamp(),
      playerUsernames: [this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      previousTurnResult: {
        answers: [
          prevPlayerAnswer,
          remoteAnswer1
        ],
        promptText: remoteAnswer1.instantiatedPromptText,
        resultMsg: "",
        scoreEffects: {
          scoreEffectEvenSplit: false,
          scoreEffectGreatMinds: false,
          scoreEffectTeamPlayer: true
        }
      },
      turnInfo: {
        answers: [
          playerAnswer,
          this.demoAnswers.getRemoteUser2Answer1()
        ],
        canPlayerVote: false,
        promptText: this.demoAnswers.getRemoteUser2Answer1().instantiatedPromptText,
        roundIndex: 0,
        turnIndex: 1,
        wereAllAnswersSubmitted: this.demoAnswers.didPlayerSubmitResponse[1]
      }
    }
  }
  getVotingState2(prevPlayerAnswer) {
    let remoteAnswer2 = this.demoAnswers.getRemoteUser2Answer1();
    this.playerScore = this.playerScore + prevPlayerAnswer.score;
    this.remoteScore2 = this.remoteScore2 + remoteAnswer2.score;
    return {
      gameStatus: 'playing',
      roundIndex: 0,
      turnIndex: 2,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getSubsequentVotingCountdownTimeStamp(),
      playerUsernames: [this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      previousTurnResult: {
        answers: [
          prevPlayerAnswer,
          remoteAnswer2
        ],
        promptText: remoteAnswer2.instantiatedPromptText,
        resultMsg: "",
        scoreEffects: {
          scoreEffectEvenSplit: false,
          scoreEffectGreatMinds: false,
          scoreEffectTeamPlayer: true
        }
      },
      turnInfo: {
        answers: [
          this.demoAnswers.getRemoteUser3Answer1(true),
          this.demoAnswers.getRemoteUser1Answer2(true)
        ],
        canPlayerVote: true,
        promptText: this.demoAnswers.getRemoteUser3Answer1(true).instantiatedPromptText,
        roundIndex: 0,
        turnIndex: 2,
        wereAllAnswersSubmitted: true
      }
    }
  }
  getVotingState3(didPlayerVote: boolean) {
    let remoteAnswer1 = this.demoAnswers.getRemoteUser1Answer2(didPlayerVote);
    let remoteAnswer3 = this.demoAnswers.getRemoteUser3Answer1(didPlayerVote);
    this.remoteScore1 = this.remoteScore1 + remoteAnswer1.score;
    this.remoteScore3 = this.remoteScore3 + remoteAnswer3.score;
    return {
      gameStatus: 'playing',
      roundIndex: 0,
      turnIndex: 3,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getSubsequentVotingCountdownTimeStamp(),
      playerUsernames: [this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      previousTurnResult: {
        answers: [
          remoteAnswer3,
          remoteAnswer1
        ],
        promptText: remoteAnswer1.instantiatedPromptText,
        resultMsg: "",
        scoreEffects: {
          scoreEffectEvenSplit: didPlayerVote? true : false,
          scoreEffectGreatMinds: false,
          scoreEffectTeamPlayer: false
        }
      },
      turnInfo: {
        answers: [
          this.demoAnswers.getRemoteUser2Answer2(didPlayerVote),
          this.demoAnswers.getRemoteUser3Answer2(didPlayerVote)
        ],
        canPlayerVote: true, 
        promptText: this.demoAnswers.getRemoteUser2Answer2(didPlayerVote).instantiatedPromptText,
        roundIndex: 0,
        turnIndex: 3,
        wereAllAnswersSubmitted: true
      }
    }
  }
  GoToNextRound(didPlayerVote: boolean) {
    let remoteAnswer2 = this.demoAnswers.getRemoteUser2Answer2(didPlayerVote);
    let remoteAnswer3 = this.demoAnswers.getRemoteUser3Answer2(didPlayerVote);
    this.remoteScore2 = this.remoteScore2 + remoteAnswer2.score;
    this.remoteScore3 = this.remoteScore3 + remoteAnswer3.score;

    let scoreboard = [
      {
        username: this.player.name,
        score: this.playerScore,
        primaryColor: this.player.primary
      },
      {
        username: this.remoteUser1.name,
        score: this.remoteScore1,
        primaryColor: this.remoteUser1.primary
      },
      {
        username: this.remoteUser2.name,
        score: this.remoteScore2,
        primaryColor: this.remoteUser2.primary
      },
      {
        username: this.remoteUser3.name,
        score: this.remoteScore3,
        primaryColor: this.remoteUser3.primary
      }
    ]

    scoreboard.sort((entryA, entryB) =>
      entryA.score < entryB.score ? 1 : -1
    );

    return {
      gameStatus: 'playing',
      roundIndex: 1,
      turnIndex: -1,
      numRounds: 1,
      numTurns: 3,
      hostUserId: this.player.id,
      countdownTimestamp: this.getSubsequentVotingCountdownTimeStamp(),
      playerUsernames: [this.player.name, this.remoteUser1.name, this.remoteUser2.name, this.remoteUser3.name],
      playerUserIds: [ this.player.id , this.remoteUser1.id, this.remoteUser2.id, this.remoteUser3.id ],
      playerPrimaryColors: [ this.player.primary, this.remoteUser1.primary, this.remoteUser2.primary, this.remoteUser3.primary ],
      playerSecondaryColors: [ this.player.secondary, this.remoteUser1.secondary, this.remoteUser2.secondary, this.remoteUser3.secondary ],
      playerBackgroundColors: [ this.player.background, this.remoteUser1.background, this.remoteUser2.background, this.remoteUser3.background ],
      previousTurnResult: {
        answers: [
          remoteAnswer2,
          remoteAnswer3
        ],
        promptText: remoteAnswer2.instantiatedPromptText,
        resultMsg: "",
        scoreEffects: {
          scoreEffectEvenSplit: didPlayerVote? true : false,
          scoreEffectGreatMinds: false,
          scoreEffectTeamPlayer: false
        }
      },
      scoreboard
    }
  }
}